

































































import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import Toast from '@/components/display/toast.vue';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import { VatModals } from './models/vat-modals';
import { VatService } from './service/vat-service';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Vat, GraphsApi, Sensor, Transceiver } from '@/api';
import moment from 'moment';

@Component({
	components: {
		SaveCancelButtons,
	},
})
export default class EditEmptyDistanceModal extends Vue {
	@Prop({ required: true, type: Object })
	public vat!: Vat & { transceiver: Transceiver; sensors: Sensor[] };
	public emptyDistance: number = this.vat.emptyDistance ?? 0;
	public MODALID: string = VatModals.EMPTY_VOLUME;
	public savingEmptyDistance: boolean = false;
	public graphError: boolean = false;
	public graphLoading: boolean = false;
	public chart?: am4charts.XYChart;
	public series: string | undefined;
	public sensor?: Sensor = this.vat.sensors.filter(
		(elem: any) => elem.type === 'lidarDistance' || elem.type === 'radarDistance',
	)[0];
	public toast = new Toast();
	public dateOffset: number = 0;
	private readonly vatService: VatService = new VatService();

	public get startDate() {
		return moment().subtract(this.dateOffset + 1, 'months');
	}

	public get endDate() {
		return moment().subtract(this.dateOffset, 'months');
	}

	public get checkLimits(): boolean {
		return this.emptyDistance <= 6000 && this.emptyDistance >= 0;
	}

	public get disableSave(): boolean {
		return !this.checkLimits || this.savingEmptyDistance;
	}

	public get endIsCurrent() {
		return this.endDate.format('MMMM YYYY') === moment().format('MMMM YYYY');
	}

	public changeOffset(num: number): void {
		this.graphLoading = true;
		this.dateOffset += num;
		this.graphData();
	}

	public onOpen(): void {
		this.emptyDistance = this.vat.emptyDistance ?? 0;
		this.graphData();
	}

	public close(): void {
		this.chart?.dispose();
		this.$bvModal.hide(`${this.MODALID}-${this.vat.id}`);
	}

	public handleInput(inputText: number): void {
		if (!this.chart) {
			this.graphError = true;
			return;
		}
		this.addEmptyDistanceLine(this.chart.yAxes.values[0], inputText);
	}

	public async submit(): Promise<void> {
		if (this.emptyDistance == null) {
			return;
		}
		this.savingEmptyDistance = true;
		const res = await this.vatService.updateEmptyDistance(this.vat.id, this.emptyDistance);

		if (res === undefined) {
			this.vat.emptyDistance = this.emptyDistance;
			this.close();
			this.toast.makeToast(this.vat.label ?? 'Vat', 'Empty distance updated', 'success');
		} else {
			this.toast.makeToast(
				'Error',
				`There was a problem updating the offset temperature: ${res.errorMessage}`,
				'danger',
				false,
			);
		}
		this.savingEmptyDistance = false;
	}

	public async graphData() {
		this.graphLoading = true;
		this.graphError = false;
		if (!this.vat || !this.sensor || !this.sensor.id) {
			this.graphError = true;
			return;
		}
		try {
			const { data }: any = await new GraphsApi().graph(
				'sensor',
				this.sensor.id,
				this.startDate.valueOf(),
				this.endDate.valueOf(),
				this.series,
			);
			await this.loadChart(data.data.distance);
		} catch (e) {
			this.graphError = true;
		}
		this.graphLoading = false;
	}

	public async loadChart(distances: any) {
		if (this.chart) {
			this.chart.dispose();
		}

		this.chart = am4core.create('emptyDistance-chartdiv' + this.vat.id, am4charts.XYChart);
		this.initChart();

		const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
		const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
		valueAxis.min = 0;

		this.configureDateAxis(dateAxis);
		this.createSeries(distances, valueAxis);
		this.addEmptyDistanceLine(valueAxis);
		this.addVatHeightLine(valueAxis);
	}

	private initChart() {
		if (!this.chart) {
			this.graphError = true;
			return;
		}
		am4core.options.minPolylineStep = 5;
		this.chart.colors.step = 3;
		this.chart.maskBullets = false;
		this.chart.scrollbarX = new am4core.Scrollbar();
		this.chart.legend = new am4charts.Legend();
		this.chart.cursor = new am4charts.XYCursor();
	}

	private configureDateAxis(dateAxis: am4charts.DateAxis) {
		dateAxis.renderer.minGridDistance = 50;
		dateAxis.baseInterval = { timeUnit: 'minute', count: 5 }; // timeUnit: "minute", count: 5
		dateAxis.tooltipDateFormat = '[bold]dd-MM-yy, h:mm:ss a[/]';
		dateAxis.renderer.grid.template.location = 0;

		(dateAxis.min = this.startDate.valueOf()),
			(dateAxis.max = this.endDate.valueOf()),
			(dateAxis.dateFormatter = new am4core.DateFormatter());
		dateAxis.dateFormatter.dateFormat = 'MM-dd';
		dateAxis.renderer.axisFills.template.disabled = false;
		dateAxis.renderer.axisFills.template.fill = am4core.color('#F6861F');
		dateAxis.renderer.axisFills.template.fillOpacity = 0.2;
	}

	private addEmptyDistanceLine(
		valueAxis:
			| am4charts.Axis<am4charts.AxisRendererY>
			| am4charts.ValueAxis<am4charts.AxisRenderer>,
		value = this.emptyDistance,
	) {
		if (value && this.chart && this.checkLimits) {
			if (this.chart.series.length > 0) {
				this.chart.series.values
					.filter((s: any) => s)
					.forEach(series => {
						const hexColor = (series as any).stroke?.hex ?? '';
						if (this.chart && series && hexColor === '#e00000') {
							this.chart.series.removeValue(series);
						}
					});
			}

			this.createConstantSeries(
				value,
				`Empty Distance: ${this.emptyDistance} mm`,
				'#e00000',
				valueAxis,
			);
		}
	}

	private addVatHeightLine(valueAxis: am4charts.ValueAxis) {
		if (this.vat.vatOffset && this.vat.height) {
			const heightWithOffset = this.vat.height + this.vat.vatOffset;
			return this.createConstantSeries(
				heightWithOffset,
				`Vat Offset & Height: ${heightWithOffset} mm`,
				'#000000',
				valueAxis,
			);
		}
	}

	private createConstantSeries(
		value: number,
		name: string,
		color: string,
		valueAxis:
			| am4charts.Axis<am4charts.AxisRendererY>
			| am4charts.ValueAxis<am4charts.AxisRenderer>,
	) {
		if (!this.chart) {
			this.graphError = true;
			return;
		}
		const series = this.chart.series.push(new am4charts.LineSeries());
		series.dataFields.valueY = 'value';
		series.dataFields.dateX = 'timestamp';
		series.strokeDasharray = '3, 3';
		series.strokeWidth = 2;
		series.yAxis = valueAxis;
		series.name = name;
		series.stroke = am4core.color(color);
		series.data = [
			{ timestamp: this.startDate.valueOf(), value },
			{ timestamp: this.endDate.valueOf(), value },
		];
	}

	public createSeries(distance: any, valueAxis: am4charts.ValueAxis) {
		if (!this.chart) {
			this.graphError = true;
			return;
		}
		const series = this.chart.series.push(new am4charts.LineSeries());
		series.dataFields.valueY = 'value';
		series.dataFields.dateX = 'timestamp';
		series.strokeWidth = 2;
		series.yAxis = valueAxis;
		series.name = 'Distance';
		series.tooltipText = '{name}: [bold]{valueY}[/]';
		series.connect = true;
		series.data = distance;
	}
}
